import {
  BotIcon,
  BotMessageSquareIcon,
  CloudSyncIcon,
  GitForkIcon,
  GitPullRequestArrowIcon,
  PencilIcon,
  ShieldCheckIcon,
  SquareTerminalIcon,
} from "lucide-react";
import React, { useMemo } from "react";

import { TransformTool } from "@/apollo/types";
import { GitHubIcon } from "@/components/icons/domain-icons";
import { Badge } from "@/components/elements/Badge";
import { DbtCoreIcon, DbtIcon } from "@/components/icons/dbt";
import { IntegrationLogo } from "@/integrations";

import { useFlags } from "launchdarkly-react-client-sdk";
import { AgentReadyCard, AgentReadyCardCompact } from "../ui/AgentReadyCard";
import { DbtContent } from "../ui/dbt/DbtContent";
import { GitHubContent } from "../ui/github/GitHubContent";

export type TransformToolOption = {
  value: TransformTool;
  label: string;
  title?: string;
  hideTitle?: boolean;
  Icon: React.ComponentType<{ className?: string }>;
  tagline: React.ReactNode;
  content?: React.ReactNode;
  features: React.ReactNode;
  tag?: React.ReactNode;
  docs?: string;
  highlighted?: boolean;
  aiPowered?: boolean;
  badge?: React.ReactNode;
  comingSoon?: boolean;
  footerCallout?: React.ReactNode;
  featureFlag?: string;
  featureFlagText?: React.ReactNode;
};

export function useTransformToolOptions() {
  const { githubIntegrationEnabled, githubIntegrationFeatureFlag } = useFlags();

  return useMemo(() => {
    const transformToolOptions: Record<
      Extract<
        TransformTool,
        | TransformTool.Github
        | TransformTool.Weld
        | TransformTool.Dbt
        | TransformTool.DbtCore
        | TransformTool.Other
      >,
      TransformToolOption
    > = {
      [TransformTool.Github]: {
        value: TransformTool.Github,
        label: "GitHub",
        title: "Weld Transform Engine with GitHub",
        Icon: (props: { className?: string }) => <GitHubIcon {...props} />,
        tagline: (
          <>
            Use <strong>GitHub</strong> to manage SQL transformations in your
            warehouse. <strong>Weld</strong> handles ingestion and sync.
          </>
        ),
        docs: "https://weld.app/docs/transformations/github-sync",
        content: <GitHubContent />,
        features: (
          <ul className="list-outside space-y-1.5">
            <li className="flex items-center gap-2">
              <GitPullRequestArrowIcon className="inline size-4 shrink-0" />
              <span>Use GitHub for version control, PRs, and code review.</span>
            </li>
            <li className="flex items-center gap-2">
              <BotIcon className="inline size-4 shrink-0" />
              <span>
                Agents and developers author model changes through GitHub.
              </span>
            </li>
            <li className="flex items-center gap-2">
              <CloudSyncIcon className="inline size-4 shrink-0" />
              <span>
                Sync transforms to Weld on{" "}
                <code className="mx-0.5 rounded-xs bg-muted px-0.5 text-sm whitespace-nowrap outline-2 outline-muted outline-solid">
                  git push
                </code>
              </span>
            </li>
            <li className="flex items-center gap-2">
              <SquareTerminalIcon className="inline size-4 shrink-0" />
              <span>
                Test, validate, and run transforms via Weld Connect API and MCP
                Server.
              </span>
            </li>
            <li className="flex items-center gap-2">
              <GitForkIcon className="inline size-4 shrink-0" />
              <span>
                Weld powers scheduling, lineage, alerting, ingestion, and
                reverse ETL.
              </span>
            </li>
          </ul>
        ),
        highlighted: true,
        footerCallout: <AgentReadyCard />,
        featureFlag: githubIntegrationFeatureFlag?.trim() || undefined,
        featureFlagText:
          (githubIntegrationFeatureFlag ?? "").trim().toLowerCase() ===
          "beta" ? (
            <span className="text-violet-700 dark:text-violet-300">
              This integration is in beta. We're actively developing it.
            </span>
          ) : undefined,
      },
      [TransformTool.Weld]: {
        value: TransformTool.Weld,
        label: "Weld Transform Engine",
        Icon: (props: { className?: string }) => (
          <IntegrationLogo {...props} id="weld" />
        ),
        tagline: (
          <>
            <strong>Weld</strong> will handle both data ingestion and
            transformation in your warehouse.
          </>
        ),
        docs: "https://weld.app/docs/transformations",
        features: (
          <ul className="list-outside space-y-1.5">
            <li className="flex items-center gap-2">
              <PencilIcon className="inline size-4 shrink-0" />
              <span>Edit and manage models directly in the Weld UI.</span>
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheckIcon className="inline size-4 shrink-0" />
              <span>Use Weld-native version control.</span>
            </li>
            <li className="flex items-center gap-2">
              <BotMessageSquareIcon className="inline size-4 shrink-0" />
              <span>
                Agents author model changes via Transform API and MCP Server.
              </span>
            </li>
            <li className="flex items-center gap-2">
              <SquareTerminalIcon className="inline size-4 shrink-0" />
              <span>
                Test, validate, and run transforms via Weld Connect API and MCP
                Server.
              </span>
            </li>
            <li className="flex items-center gap-2">
              <GitForkIcon className="inline size-4 shrink-0" />
              <span>
                Weld powers scheduling, lineage, alerting, ingestion, and
                reverse ETL.
              </span>
            </li>
          </ul>
        ),
        tag: (
          <Badge variant="secondary" className="border-border shadow-xs">
            advanced
          </Badge>
        ),
        highlighted: true,
        footerCallout: <AgentReadyCard />,
      },
      [TransformTool.Dbt]: {
        value: TransformTool.Dbt,
        label: "dbt",
        hideTitle: true,
        Icon: DbtIcon,
        tagline: (
          // and can trigger dbt jobs via webhooks
          <>
            Use <strong>dbt</strong> to model data in your warehouse.{" "}
            <strong>Weld</strong> handles ingestion and sync — and can trigger
            dbt jobs via webhooks.
          </>
        ),
        docs: "https://weld.app/docs/transformations/third-party-orchestration/dbt",
        content: <DbtContent />,
        features: (
          <ul className="list-outside list-disc space-y-1 pl-4">
            <li>Fully managed by dbt Labs (formerly dbt Cloud).</li>
            <li>Modular SQL models & Jinja macros.</li>
            <li>Git-based version control.</li>
            <li>
              <strong>Weld powers ingestion & reverse ETL.</strong>
            </li>
            <li>Trigger dbt jobs from Weld.</li>
          </ul>
        ),
        tag: (
          <Badge
            variant="secondary"
            className="border-border leading-normal shadow-xs"
          >
            managed
          </Badge>
        ),
        footerCallout: <AgentReadyCardCompact />,
      },
      [TransformTool.DbtCore]: {
        value: TransformTool.DbtCore,
        label: "dbt Core",
        hideTitle: true,
        Icon: DbtCoreIcon,
        tagline: (
          <>
            Use <strong>dbt Core</strong> to model data in your warehouse.{" "}
            <strong>Weld</strong> handles ingestion and sync.
          </>
        ),
        docs: "https://weld.app/docs/transformations/third-party-orchestration/dbt-core",
        features: (
          <ul className="list-outside list-disc space-y-1 pl-4">
            <li>Open source, self-hosted dbt.</li>
            <li>Modular SQL models & Jinja macros.</li>
            <li>Git-based version control.</li>
            <li>
              <strong>Weld powers ingestion & reverse ETL.</strong>
            </li>
            <li>
              <div>
                <span className="pr-2">Trigger dbt Core jobs from Weld</span>
                <Badge variant="tertiary" className="relative -top-px">
                  coming soon
                </Badge>
              </div>
            </li>
          </ul>
        ),
        tag: (
          <Badge
            variant="secondary"
            className="border-border leading-normal shadow-xs"
          >
            self-hosted
          </Badge>
        ),
        footerCallout: <AgentReadyCardCompact />,
      },
      [TransformTool.Other]: {
        value: TransformTool.Other,
        label: "Other / directly in my warehouse",
        Icon: (_props: { className?: string }) => <></>,
        tagline: (
          <>
            Use your <strong>existing tools</strong> for transformation.{" "}
            <strong>Weld</strong> handles ingestion and sync.
          </>
        ),
        docs: "https://weld.app/docs/transformations/third-party-orchestration/webhooks",
        features: (
          <div>
            <ul className="list-outside list-disc space-y-1 pl-4">
              <li>Keep using existing tools and workflows.</li>
              <li>
                <strong>Weld powers ingestion & reverse ETL.</strong>
              </li>
              <li>
                <span className="pr-2">trigger workflows with webhooks</span>
                <Badge variant="tertiary" className="relative -top-px">
                  coming soon
                </Badge>
              </li>
              <li>Scheduling, lineage, and alerting.</li>
            </ul>
            <div className="mt-4 flex items-center justify-around gap-3 px-8 opacity-60 saturate-0">
              <IntegrationLogo id="bigquery" className="size-6" />
              <IntegrationLogo id="snowflake" className="size-6" />
              <IntegrationLogo id="databricks" className="size-6" />
              <IntegrationLogo id="redshift" className="size-6" />
            </div>
          </div>
        ),
        footerCallout: <AgentReadyCardCompact />,
      },
    };

    return {
      optionsMap: transformToolOptions,
      availableOptions: Object.values(transformToolOptions).filter((option) => {
        if (
          option.value === TransformTool.Github &&
          !githubIntegrationEnabled
        ) {
          return false;
        }
        return true;
      }),
    };
  }, [githubIntegrationEnabled, githubIntegrationFeatureFlag]);
}
